import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { Sprite, Graphics, Assets, BlurFilter, Rectangle } from 'pixi.js';
import { ChartRenderer } from '~/utils/ChartRenderer';
import { Box } from 'styled-system/jsx';
import type { PreProcessedChartFileSoA } from '~/types';

export interface ChartViewerHandles {
  getCanvasBlob: (
    callback: (blob: Blob | null) => void,
    mimeType?: string,
    quality?: number,
    drawBackground?: boolean
  ) => void;
}

interface ChartViewerProps {
  chart: PreProcessedChartFileSoA | null | undefined;
  feverSection?: number;
  sectionTimes?: number[];
  imageUrl?: string;
}

export const ChartViewer = forwardRef<ChartViewerHandles, ChartViewerProps>(
  ({ chart, feverSection, sectionTimes, imageUrl }, ref) => {
    const pixiContainerRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<ChartRenderer | undefined>(undefined);
    const [isRendererReady, setIsRendererReady] = useState(false);
    const currentBlobOperationIdRef = useRef<symbol | null>(null);

    useImperativeHandle(ref, () => ({
      getCanvasBlob: (callback, mimeType = 'image/png', quality, drawBackground = true) => {
        const operationId = Symbol('blobOp');
        currentBlobOperationIdRef.current = operationId;

        const rendererInstance = rendererRef.current;
        if (!rendererInstance?.app?.stage || !rendererInstance?.app?.renderer) {
          callback(null);
          return;
        }

        const initialPixiRenderer = rendererInstance.app.renderer;
        const initialScreenWidth = initialPixiRenderer.width / initialPixiRenderer.resolution;
        const initialScreenHeight = initialPixiRenderer.height / initialPixiRenderer.resolution;

        let addedBgElement: Sprite | Graphics | null = null;
        let addedOverlayElement: Graphics | null = null;

        const cleanupTemporaryElements = () => {
          const currentApp = rendererRef.current?.app;

          if (addedBgElement) {
            if (currentApp?.stage && addedBgElement.parent === currentApp.stage) {
              currentApp.stage.removeChild(addedBgElement);
            }
            if (!addedBgElement.destroyed) {
              addedBgElement.destroy({ children: true });
            }
            addedBgElement = null;
          }
          if (addedOverlayElement) {
            if (currentApp?.stage && addedOverlayElement.parent === currentApp.stage) {
              currentApp.stage.removeChild(addedOverlayElement);
            }
            if (!addedOverlayElement.destroyed) {
              addedOverlayElement.destroy({ children: true });
            }
            addedOverlayElement = null;
          }
        };

        const cleanupAndCallbackNull = () => {
          cleanupTemporaryElements();
          callback(null);
        };

        const getActiveContext = () => {
          if (currentBlobOperationIdRef.current !== operationId) {
            cleanupAndCallbackNull();
            return null;
          }
          const currentRendererInstance = rendererRef.current;
          if (!currentRendererInstance?.app?.renderer || !currentRendererInstance?.app?.stage) {
            cleanupAndCallbackNull();
            return null;
          }
          const { renderer, stage } = currentRendererInstance.app;
          return {
            renderer,
            stage,
            screenWidth: renderer.width / renderer.resolution,
            screenHeight: renderer.height / renderer.resolution
          };
        };

        const performExtraction = () => {
          const context = getActiveContext();
          if (!context) return;
          const { renderer, stage, screenWidth, screenHeight } = context;

          try {
            const frame = new Rectangle(0, 0, screenWidth, screenHeight);
            const extractedCanvas = renderer.extract.canvas({ target: stage, frame });

            if (extractedCanvas) {
              extractedCanvas.toBlob?.(
                (blob) => {
                  if (currentBlobOperationIdRef.current !== operationId) {
                    cleanupAndCallbackNull();
                    return;
                  } else {
                    callback(blob);
                    cleanupTemporaryElements();
                  }
                },
                mimeType,
                quality
              );
            } else {
              cleanupAndCallbackNull();
            }
          } catch {
            cleanupAndCallbackNull();
          }
        };

        const addFallbackBackground = (width: number, height: number) => {
          const context = getActiveContext();
          if (!context) return;

          const fallbackBg = new Graphics();
          fallbackBg.rect(0, 0, width, height).fill({ color: 0x000000, alpha: 1 });
          context.stage.addChildAt(fallbackBg, 0);
          addedBgElement = fallbackBg;
          performExtraction();
        };

        if (imageUrl && drawBackground) {
          Assets.load(imageUrl)
            .then((texture) => {
              if (currentBlobOperationIdRef.current !== operationId) {
                cleanupAndCallbackNull();
                return;
              }
              const context = getActiveContext();
              if (!context) return;
              const { stage, screenWidth, screenHeight } = context;

              const backgroundSprite = Sprite.from(texture);
              const imageAspectRatio = texture.width / texture.height;
              const rendererAspectRatio = screenWidth / screenHeight;

              if (rendererAspectRatio > imageAspectRatio) {
                backgroundSprite.width = screenWidth;
                backgroundSprite.height = screenWidth / imageAspectRatio;
              } else {
                backgroundSprite.height = screenHeight;
                backgroundSprite.width = screenWidth * imageAspectRatio;
              }
              backgroundSprite.anchor.set(0.5);
              backgroundSprite.x = screenWidth / 2;
              backgroundSprite.y = screenHeight / 2;

              const blurFilter = new BlurFilter();
              blurFilter.strength = 8;
              backgroundSprite.filters = [blurFilter];

              stage.addChildAt(backgroundSprite, 0);
              addedBgElement = backgroundSprite;

              const overlayGraphics = new Graphics();
              overlayGraphics
                .rect(0, 0, screenWidth, screenHeight)
                .fill({ color: 0x000000, alpha: 0.75 });
              stage.addChildAt(overlayGraphics, 1);
              addedOverlayElement = overlayGraphics;

              performExtraction();
            })
            .catch(() => {
              if (currentBlobOperationIdRef.current !== operationId) {
                cleanupTemporaryElements();
                callback(null);
                return;
              }

              const fallbackWidth = rendererRef.current?.app?.renderer
                ? rendererRef.current.app.renderer.width /
                  rendererRef.current.app.renderer.resolution
                : initialScreenWidth;
              const fallbackHeight = rendererRef.current?.app?.renderer
                ? rendererRef.current.app.renderer.height /
                  rendererRef.current.app.renderer.resolution
                : initialScreenHeight;
              addFallbackBackground(fallbackWidth, fallbackHeight);
            });
        } else {
          addFallbackBackground(initialScreenWidth, initialScreenHeight);
        }
      }
    }));

    useEffect(() => {
      let isMounted = true;
      const chartRendererInstance = new ChartRenderer();

      chartRendererInstance
        .initialize()
        .then(() => {
          if (isMounted) {
            rendererRef.current = chartRendererInstance;
            if (pixiContainerRef.current && chartRendererInstance.canvas) {
              pixiContainerRef.current.innerHTML = '';
              pixiContainerRef.current.appendChild(chartRendererInstance.canvas);
            }
            setIsRendererReady(true);
          } else {
            if (chartRendererInstance.app) {
              chartRendererInstance.app.destroy(true);
            }
          }
        })
        .catch(() => {
          if (chartRendererInstance.app) {
            try {
              chartRendererInstance.app.destroy(true);
            } catch {}
          }
          if (isMounted) {
            setIsRendererReady(false);
          }
        });
      return () => {
        isMounted = false;
        setIsRendererReady(false);
        currentBlobOperationIdRef.current = null;
        if (chartRendererInstance.app) {
          try {
            chartRendererInstance.app.destroy(true);
          } catch {
            /* Ignore destroy error in cleanup */
          }
        }
        if (rendererRef.current === chartRendererInstance) {
          rendererRef.current = undefined;
        }
        if (pixiContainerRef.current) {
          pixiContainerRef.current.innerHTML = '';
        }
      };
    }, []);

    useEffect(() => {
      if (isRendererReady && rendererRef.current && chart) {
        rendererRef.current.renderChart(chart, sectionTimes, feverSection);
      }
    }, [chart, sectionTimes, feverSection, isRendererReady]);

    return <Box ref={pixiContainerRef} />;
  }
);
